import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Stack,
  Button,
  Step,
  StepLabel,
  Stepper,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Snackbar,
  Divider,
  Skeleton,
  Chip,
} from '@mui/material';
import { ArrowBack, NavigateNext, NavigateBefore, Save } from '@mui/icons-material';

import hrService, {
  Department,
  Designation,
  EmployeeListItem,
  EmployeePayload,
  EmploymentType,
} from '../../services/hrService';
import { PageHeader, ErrorState } from '../../components/common/pageKit';
import { formatIndianNumber } from '../../utils/currency';

// ---------------------------------------------------------------------------
// Employee multi-step wizard — create at /hr/employees/new, edit at
// /hr/employees/:id/edit. The backend accepts a wider set of camelCase
// fields than hrService's EmployeePayload type declares (personal/contact/
// employment/statutory columns added by migration 063 — see src/models/Hr.js
// createEmployee/updateEmployee). EmployeeWizardPayload extends EmployeePayload
// with those extra fields; since it's a superset, passing a variable of this
// type to hrService.createEmployee/updateEmployee type-checks without a cast
// (excess-property checks only fire on object literals, not variables).
// ---------------------------------------------------------------------------

interface EmployeeWizardPayload extends EmployeePayload {
  title?: string;
  middleName?: string;
  gender?: string;
  maritalStatus?: string;
  bloodGroup?: string;
  nationality?: string;
  personalEmail?: string;
  alternatePhone?: string;
  currentAddress?: string;
  permanentAddress?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  probationMonths?: number;
  noticePeriodDays?: number;
  grade?: string;
  positionTitle?: string;
  businessUnit?: string;
  taxRegime?: string;
  pfApplicable?: boolean;
  esiApplicable?: boolean;
  ptApplicable?: boolean;
}

const emptyForm: EmployeeWizardPayload = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  dateOfJoining: '',
  departmentId: undefined,
  designationId: undefined,
  managerId: undefined,
  employmentType: 'FULL_TIME',
  workLocation: '',
  ctcAnnual: undefined,
  pan: '',
  aadhaarLast4: '',
  bankAccount: '',
  bankIfsc: '',
  pfNumber: '',
  uan: '',
  esiNumber: '',
  title: '',
  middleName: '',
  gender: '',
  maritalStatus: '',
  bloodGroup: '',
  nationality: 'Indian',
  personalEmail: '',
  alternatePhone: '',
  currentAddress: '',
  permanentAddress: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  emergencyContactRelation: '',
  probationMonths: undefined,
  noticePeriodDays: undefined,
  grade: '',
  positionTitle: '',
  businessUnit: '',
  taxRegime: 'NEW',
  pfApplicable: true,
  esiApplicable: false,
  ptApplicable: true,
};

const EMPLOYMENT_TYPES: EmploymentType[] = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERN'];
const GENDERS = ['Male', 'Female', 'Other', 'Prefer not to say'];
const MARITAL_STATUSES = ['Single', 'Married', 'Divorced', 'Widowed'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const TAX_REGIMES = ['OLD', 'NEW'];

const STEPS = ['Basic', 'Contact', 'Employment', 'Statutory', 'Bank', 'Compensation', 'Review & Create'];

const fullName = (e: { first_name: string; last_name: string | null }): string =>
  [e.first_name, e.last_name].filter(Boolean).join(' ');

const EmployeeFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const editingId = id ? Number(id) : null;
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<EmployeeWizardPayload>(emptyForm);

  const [departments, setDepartments] = useState<Department[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [managers, setManagers] = useState<EmployeeListItem[]>([]);

  const [loadingLookups, setLoadingLookups] = useState(true);
  const [loadingEmployee, setLoadingEmployee] = useState(!!editingId);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const set = <K extends keyof EmployeeWizardPayload>(key: K, value: EmployeeWizardPayload[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const loadLookups = useCallback(() => {
    setLoadingLookups(true);
    Promise.all([hrService.getDepartments(), hrService.getDesignations(), hrService.getEmployees({ limit: 200 })])
      .then(([d, ds, e]) => {
        setDepartments(Array.isArray(d.departments) ? d.departments : []);
        setDesignations(Array.isArray(ds.designations) ? ds.designations : []);
        setManagers(Array.isArray(e.employees) ? e.employees : []);
      })
      .catch((err: any) => setLoadError(err.message || 'Failed to load department/designation lookups'))
      .finally(() => setLoadingLookups(false));
  }, []);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!editingId) return;
    setLoadingEmployee(true);
    setLoadError(null);
    hrService
      .getEmployeeById(editingId)
      .then((res) => {
        const e = res.employee as any;
        setForm({
          firstName: e.first_name || '',
          lastName: e.last_name || '',
          email: e.email || '',
          phone: e.phone || '',
          dateOfBirth: e.date_of_birth ? String(e.date_of_birth).slice(0, 10) : '',
          dateOfJoining: e.date_of_joining ? String(e.date_of_joining).slice(0, 10) : '',
          departmentId: e.department_id ?? undefined,
          designationId: e.designation_id ?? undefined,
          managerId: e.manager_id ?? undefined,
          employmentType: e.employment_type || 'FULL_TIME',
          workLocation: e.work_location || '',
          ctcAnnual: e.ctc_annual !== null && e.ctc_annual !== undefined ? Number(e.ctc_annual) : undefined,
          pan: e.pan || '',
          aadhaarLast4: e.aadhaar_last4 || '',
          bankAccount: e.bank_account || '',
          bankIfsc: e.bank_ifsc || '',
          pfNumber: e.pf_number || '',
          uan: e.uan || '',
          esiNumber: e.esi_number || '',
          title: e.title || '',
          middleName: e.middle_name || '',
          gender: e.gender || '',
          maritalStatus: e.marital_status || '',
          bloodGroup: e.blood_group || '',
          nationality: e.nationality || 'Indian',
          personalEmail: e.personal_email || '',
          alternatePhone: e.alternate_phone || '',
          currentAddress: e.current_address || '',
          permanentAddress: e.permanent_address || '',
          emergencyContactName: e.emergency_contact_name || '',
          emergencyContactPhone: e.emergency_contact_phone || '',
          emergencyContactRelation: e.emergency_contact_relation || '',
          probationMonths: e.probation_months ?? undefined,
          noticePeriodDays: e.notice_period_days ?? undefined,
          grade: e.grade || '',
          positionTitle: e.position_title || '',
          businessUnit: e.business_unit || '',
          taxRegime: e.tax_regime || 'NEW',
          pfApplicable: e.pf_applicable !== undefined ? !!e.pf_applicable : true,
          esiApplicable: e.esi_applicable !== undefined ? !!e.esi_applicable : false,
          ptApplicable: e.pt_applicable !== undefined ? !!e.pt_applicable : true,
        });
      })
      .catch((err: any) => setLoadError(err.message || 'Failed to load employee'))
      .finally(() => setLoadingEmployee(false));
  }, [editingId]);

  const managerOptions = useMemo(() => managers.filter((m) => m.id !== editingId), [managers, editingId]);

  // ---- Per-step validation ----
  const stepError = (step: number): string | null => {
    if (step === 0 && !form.firstName.trim()) return 'First name is required';
    if (step === 2 && !form.dateOfJoining) return 'Date of joining is required';
    return null;
  };

  const goNext = () => {
    const err = stepError(activeStep);
    if (err) {
      setSaveError(err);
      return;
    }
    setSaveError(null);
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setSaveError(null);
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const buildPayload = (): EmployeeWizardPayload => ({
    ...form,
    lastName: form.lastName || undefined,
    email: form.email || undefined,
    phone: form.phone || undefined,
    dateOfBirth: form.dateOfBirth || undefined,
    workLocation: form.workLocation || undefined,
    pan: form.pan || undefined,
    aadhaarLast4: form.aadhaarLast4 || undefined,
    bankAccount: form.bankAccount || undefined,
    bankIfsc: form.bankIfsc || undefined,
    pfNumber: form.pfNumber || undefined,
    uan: form.uan || undefined,
    esiNumber: form.esiNumber || undefined,
    title: form.title || undefined,
    middleName: form.middleName || undefined,
    gender: form.gender || undefined,
    maritalStatus: form.maritalStatus || undefined,
    bloodGroup: form.bloodGroup || undefined,
    nationality: form.nationality || undefined,
    personalEmail: form.personalEmail || undefined,
    alternatePhone: form.alternatePhone || undefined,
    currentAddress: form.currentAddress || undefined,
    permanentAddress: form.permanentAddress || undefined,
    emergencyContactName: form.emergencyContactName || undefined,
    emergencyContactPhone: form.emergencyContactPhone || undefined,
    emergencyContactRelation: form.emergencyContactRelation || undefined,
    grade: form.grade || undefined,
    positionTitle: form.positionTitle || undefined,
    businessUnit: form.businessUnit || undefined,
    taxRegime: form.taxRegime || undefined,
  });

  const handleSubmit = async () => {
    const basicErr = stepError(0);
    const empErr = stepError(2);
    if (basicErr || empErr) {
      setSaveError(basicErr || empErr);
      setActiveStep(basicErr ? 0 : 2);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = buildPayload();
      let resultId = editingId;
      if (editingId) {
        await hrService.updateEmployee(editingId, payload);
      } else {
        const res = await hrService.createEmployee(payload);
        resultId = res.employee.id;
      }
      setToast({ msg: editingId ? 'Employee updated' : 'Employee created', severity: 'success' });
      if (resultId) {
        navigate(`/hr/employees/${resultId}/360`);
      } else {
        navigate('/hr/employees');
      }
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  if (loadingEmployee || (loadingLookups && !departments.length && !designations.length)) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={64} sx={{ mb: 2.5, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (loadError && editingId && !form.firstName) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/employees')} sx={{ mb: 1 }}>
          Back to Employees
        </Button>
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <ErrorState message={loadError} onRetry={() => window.location.reload()} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/hr/employees')} sx={{ mb: 1 }}>
        Back to Employees
      </Button>

      <PageHeader
        title={editingId ? 'Edit Employee' : 'New Employee'}
        subtitle={editingId ? `Updating ${fullName({ first_name: form.firstName, last_name: form.lastName || null })}` : 'Multi-step onboarding wizard'}
      />

      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{s}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        {/* ---------------- Step 0: Basic ---------------- */}
        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField label="Title" fullWidth size="small" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Mr / Ms / Dr" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4.5 }}>
              <TextField label="First Name" required fullWidth size="small" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4.5 }}>
              <TextField label="Middle Name" fullWidth size="small" value={form.middleName} onChange={(e) => set('middleName', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Last Name" fullWidth size="small" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Date of Birth" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Gender" fullWidth size="small" value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Marital Status" fullWidth size="small" value={form.maritalStatus} onChange={(e) => set('maritalStatus', e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {MARITAL_STATUSES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Blood Group" fullWidth size="small" value={form.bloodGroup} onChange={(e) => set('bloodGroup', e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {BLOOD_GROUPS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Nationality" fullWidth size="small" value={form.nationality} onChange={(e) => set('nationality', e.target.value)} />
            </Grid>
          </Grid>
        )}

        {/* ---------------- Step 1: Contact ---------------- */}
        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Work Email" type="email" fullWidth size="small" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Personal Email" type="email" fullWidth size="small" value={form.personalEmail} onChange={(e) => set('personalEmail', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone" fullWidth size="small" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Alternate Phone" fullWidth size="small" value={form.alternatePhone} onChange={(e) => set('alternatePhone', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Current Address" fullWidth size="small" multiline minRows={2} value={form.currentAddress} onChange={(e) => set('currentAddress', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Permanent Address" fullWidth size="small" multiline minRows={2} value={form.permanentAddress} onChange={(e) => set('permanentAddress', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">Emergency Contact</Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Name" fullWidth size="small" value={form.emergencyContactName} onChange={(e) => set('emergencyContactName', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Phone" fullWidth size="small" value={form.emergencyContactPhone} onChange={(e) => set('emergencyContactPhone', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Relation" fullWidth size="small" value={form.emergencyContactRelation} onChange={(e) => set('emergencyContactRelation', e.target.value)} />
            </Grid>
          </Grid>
        )}

        {/* ---------------- Step 2: Employment ---------------- */}
        {activeStep === 2 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Date of Joining" type="date" required fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.dateOfJoining} onChange={(e) => set('dateOfJoining', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Employment Type" fullWidth size="small" value={form.employmentType} onChange={(e) => set('employmentType', e.target.value as EmploymentType)}>
                {EMPLOYMENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Department" fullWidth size="small" value={form.departmentId ?? ''} onChange={(e) => set('departmentId', e.target.value === '' ? undefined : Number(e.target.value))}>
                <MenuItem value="">—</MenuItem>
                {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.dept_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Designation" fullWidth size="small" value={form.designationId ?? ''} onChange={(e) => set('designationId', e.target.value === '' ? undefined : Number(e.target.value))}>
                <MenuItem value="">—</MenuItem>
                {designations.map((d) => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select label="Manager" fullWidth size="small" value={form.managerId ?? ''} onChange={(e) => set('managerId', e.target.value === '' ? undefined : Number(e.target.value))}>
                <MenuItem value="">—</MenuItem>
                {managerOptions.map((m) => <MenuItem key={m.id} value={m.id}>{fullName(m)} ({m.employee_code})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Grade" fullWidth size="small" value={form.grade} onChange={(e) => set('grade', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Position" fullWidth size="small" value={form.positionTitle} onChange={(e) => set('positionTitle', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Business Unit" fullWidth size="small" value={form.businessUnit} onChange={(e) => set('businessUnit', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Work Location" fullWidth size="small" value={form.workLocation} onChange={(e) => set('workLocation', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Probation (months)" type="number" fullWidth size="small" value={form.probationMonths ?? ''} onChange={(e) => set('probationMonths', e.target.value === '' ? undefined : Number(e.target.value))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Notice Period (days)" type="number" fullWidth size="small" value={form.noticePeriodDays ?? ''} onChange={(e) => set('noticePeriodDays', e.target.value === '' ? undefined : Number(e.target.value))} />
            </Grid>
          </Grid>
        )}

        {/* ---------------- Step 3: Statutory ---------------- */}
        {activeStep === 3 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="PAN" fullWidth size="small" value={form.pan} onChange={(e) => set('pan', e.target.value.toUpperCase())} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Aadhaar (last 4 digits)" fullWidth size="small" value={form.aadhaarLast4} inputProps={{ maxLength: 4 }} onChange={(e) => set('aadhaarLast4', e.target.value.replace(/\D/g, '').slice(0, 4))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="UAN" fullWidth size="small" value={form.uan} onChange={(e) => set('uan', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="ESI Number" fullWidth size="small" value={form.esiNumber} onChange={(e) => set('esiNumber', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="PF Number" fullWidth size="small" value={form.pfNumber} onChange={(e) => set('pfNumber', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select label="Tax Regime" fullWidth size="small" value={form.taxRegime} onChange={(e) => set('taxRegime', e.target.value)}>
                {TAX_REGIMES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">Statutory Applicability</Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel control={<Checkbox checked={!!form.pfApplicable} onChange={(e) => set('pfApplicable', e.target.checked)} />} label="PF Applicable" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel control={<Checkbox checked={!!form.esiApplicable} onChange={(e) => set('esiApplicable', e.target.checked)} />} label="ESI Applicable" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel control={<Checkbox checked={!!form.ptApplicable} onChange={(e) => set('ptApplicable', e.target.checked)} />} label="PT Applicable" />
            </Grid>
          </Grid>
        )}

        {/* ---------------- Step 4: Bank ---------------- */}
        {activeStep === 4 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Bank Account Number" fullWidth size="small" value={form.bankAccount} onChange={(e) => set('bankAccount', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Bank IFSC" fullWidth size="small" value={form.bankIfsc} onChange={(e) => set('bankIfsc', e.target.value.toUpperCase())} />
            </Grid>
          </Grid>
        )}

        {/* ---------------- Step 5: Compensation ---------------- */}
        {activeStep === 5 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="CTC (Annual)"
                type="number"
                fullWidth
                size="small"
                value={form.ctcAnnual ?? ''}
                onChange={(e) => set('ctcAnnual', e.target.value === '' ? undefined : Number(e.target.value))}
                InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>₹</Typography> }}
              />
            </Grid>
            {form.ctcAnnual !== undefined && form.ctcAnnual > 0 && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Chip label={formatIndianNumber(form.ctcAnnual)} color="primary" variant="outlined" />
              </Grid>
            )}
          </Grid>
        )}

        {/* ---------------- Step 6: Review & Create ---------------- */}
        {activeStep === 6 && (
          <Stack spacing={2.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Review</Typography>
            <Grid container spacing={2}>
              {[
                ['Name', [form.title, form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ')],
                ['Date of Joining', form.dateOfJoining || '—'],
                ['Employment Type', form.employmentType?.replace('_', ' ')],
                ['Department', departments.find((d) => d.id === form.departmentId)?.dept_name || '—'],
                ['Designation', designations.find((d) => d.id === form.designationId)?.title || '—'],
                ['Manager', managerOptions.find((m) => m.id === form.managerId) ? fullName(managerOptions.find((m) => m.id === form.managerId)!) : '—'],
                ['Work Location', form.workLocation || '—'],
                ['CTC (Annual)', form.ctcAnnual ? formatIndianNumber(form.ctcAnnual) : '—'],
                ['Email', form.email || '—'],
                ['Phone', form.phone || '—'],
                ['PAN', form.pan || '—'],
                ['Bank Account', form.bankAccount || '—'],
              ].map(([label, value]) => (
                <Grid key={label as string} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{value as string}</Typography>
                </Grid>
              ))}
            </Grid>
            <Alert severity="info">
              Review the details above, then {editingId ? 'save changes to' : 'create'} this employee record. You will be taken to their Employee 360 page.
            </Alert>
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" justifyContent="space-between">
          <Button startIcon={<NavigateBefore />} disabled={activeStep === 0 || saving} onClick={goBack}>
            Back
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button variant="contained" endIcon={<NavigateNext />} onClick={goNext}>
              Next
            </Button>
          ) : (
            <Button variant="contained" startIcon={<Save />} onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Employee'}
            </Button>
          )}
        </Stack>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3200} onClose={() => setToast(null)}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default EmployeeFormPage;
